import { ChangeDetectionStrategy, Component, EventEmitter, inject, model, OnInit, signal, WritableSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActionLogDirective } from '@user/service/logging/actionlog.directive';
import { CustomValidators } from '@shared/custom-validators';
import { InputRestrictDirective } from '@shared/directives/input-restrict.directive';
import { FormValidValidatorDirective } from '@shared/form-validator.directive';
import { ValidatorDirective } from '@shared/validator.directive';
import { ValidatorFactory } from '@shared/validator.factory';
import { ModalComponent } from '@shared/modal/modal.component';
import { ProjectHeadVO } from '../model/vo/project.vo';
import { FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ProjectSampleRequestDTO } from './project-sample-request.dto';
import { ProjectDataService } from '../service/project-data.service';
import { lastValueFrom } from 'rxjs';
import { ProjectOrderThanksComponent } from '../components/project/project-order/project-order-thanks/project-order-thanks.component';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

export type ConfigurationConsistentSamplesAction = 'back' | 'contact-sales' | undefined;

@Component({
  selector: 'app-configuration-consistent-samples',
  imports: [
    TranslateModule,
    ModalComponent,
    ReactiveFormsModule,
    ProjectOrderThanksComponent,
    ActionLogDirective,
    InputRestrictDirective,
    ValidatorDirective,
    FormsModule,
    FormValidValidatorDirective,
  ],
  templateUrl: './configuration-consistent-samples.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigurationConsistentSamplesComponent implements OnInit {
  public finish: EventEmitter<ConfigurationConsistentSamplesAction> = new EventEmitter();

  public project?: ProjectHeadVO;

  public model = model<ProjectSampleRequestDTO>(new ProjectSampleRequestDTO());

  public showSuccessPage: WritableSignal<boolean> = signal(false);
  public orderedSuccessfully: WritableSignal<boolean> = signal(false);

  private projectDataService = inject(ProjectDataService);
  private localeResourceManager = inject(LocaleResourceManager);
  private validatorFactory = inject(ValidatorFactory);
  public formValid = signal<boolean>(false);

  private validateRequireMaxLength100 = Validators.compose([Validators.required, CustomValidators.maxLength(100)])!;

  public validators: Partial<Record<keyof ProjectSampleRequestDTO, ValidatorFn | null | undefined>> = {
    salutation: Validators.maxLength(100),
    firstName: this.validateRequireMaxLength100,
    lastName: this.validateRequireMaxLength100,
    emailAddress: Validators.compose([Validators.required, Validators.email, CustomValidators.maxLength(254)]),
    phoneNumber: Validators.maxLength(100),
    customerNumber: Validators.maxLength(100),
    department: Validators.maxLength(100),
    company: this.validateRequireMaxLength100,
    street: this.validateRequireMaxLength100,
    houseNumber: Validators.compose([Validators.required, CustomValidators.maxLength(10)]),
    zipCode: Validators.compose([Validators.required, Validators.maxLength(10)]),
    city: this.validateRequireMaxLength100,
    country: this.validateRequireMaxLength100,
    amount: Validators.compose([Validators.required, this.validatorFactory.number({min: 1, max: 1000})]),
    estimatedAnnualDemand: Validators.compose([Validators.required, this.validatorFactory.number({min: 1, max: 100000})]),
    applicationDescription: Validators.required,
  }

  public cancel(): void {
    this.finish.emit();
  }

  public ngOnInit(): void {
    this.model.update((model) => {
      model.localeKey = this.localeResourceManager.currentLanguage.langKey
      model.projectVersion = this.project!.currentVersion;
      return model;
    });
  }

  public back(): void {
    this.finish.emit('back');
  }

  public contactSales(): void {
    window.open('https://www.wago.com/global/support', '_blank', "noopener");
    this.finish.emit();
  }

  public async sendSampleRequest(): Promise<void> {
    if (this.formValid() && this.project) {
      this.orderedSuccessfully.set(await lastValueFrom(this.projectDataService.sendConfigurationConsistentSampleRequest(this.project.id, this.model())));
      this.showSuccessPage.set(true);
    }
  }
}
